import { isNumber, isDecimal, compare } from '@active/core/number';
import currenciesConfig from '@active/core/currency/currenciesConfig';

export const ROUNDINGTYPES = {
  NOROUNDING: 'NO_ROUNDING',
  ROUNDINGUP: 'ROUND_HALF_UP',
  ROUNDINGDOWN: 'ROUND_HALF_DOWN',
};

/**
* Calculate pennyRoundingAmount and grandTotal.for decimal,should pass scaleFactor as 100.
*
* amount: 10.14, roundingScale: 0.3, scaleFactor: 100,
*  -> actual calculated value: 0.14 * 100, roundingScale: 0.3 * 100,
*    -> remainder: 14 % 30
*      -> compare: 14 < (30 / 2) ， rounding down
*       -> roundingAmount: - 14 / 100 = -0.14
*        -> grantTotal 10.14 + (-0.14) = 10.00
*
* amount: 10.16, roundingScale: 0.3, scaleFactor: 100,
*  -> actual calculated value: 0.16 * 100, roundingScale: 0.3 * 100,
*    -> remainder: 16 % 30
*      -> compare: 16 > (30 / 2) ， rounding up
*       -> roundingAmount: (30 - 16) / 100 = 0.14
*        -> grantTotal 10.16 + (0.14) = 10.30
*
* amount: 10.15, roundingScale: 0.3, roundingRule: ROUND_HALF_UP, scaleFactor: 100,
*  -> actual calculated value: 0.15 * 100, roundingScale: 0.3 * 100,
*    -> remainder: 15 % 30
*      -> compare: 15 = (30 / 2) ， rounding up
*       -> roundingAmount: 15 / 100 = 0.15
*         -> grantTotal 10.15 + (0.15) = 10.30
*
* amount: 10.25, roundingScale: 0.5, roundingRule: ROUND_HALF_DOWN, scaleFactor: 100,
*  -> actual calculated value: 0.25 * 100, roundingScale: 0.5 * 100,
*    -> remainder: 25 % 50
*      -> compare: 25 = (50 / 2) ， rounding down
*       -> roundingAmount: - 25 / 100 = -0.25
*         -> grantTotal 10.25 + (-0.25) = 10.00
*
* amount: 102, roundingScale: 5, roundingRule: ROUND_HALF_DOWN, scaleFactor: 1,
*  -> actual calculated value: 2, roundingScale: 5,
*    -> remainder: 2 % 5
*      -> compare: 2 < (5 / 2) ， rounding down
*       -> roundingAmount: - 2
*        -> grantTotal 102 + (-2) = 100
*
* amount: 103, roundingScale: 5, roundingRule: ROUND_HALF_DOWN, scaleFactor: 1,
*  -> actual calculated value: 3, roundingScale: 5,
*    -> remainder: 3 % 5
*      -> compare: 3 > (5 / 2) ， rounding up
*       -> roundingAmount: 5 - 3 = 2
*        -> grantTotal 103 + (2) = 105
* */

function precisionAdjuster(amount, { roundingScale, roundingRule, scaleFactor = 1 }) {
  if (roundingRule === ROUNDINGTYPES.NOROUNDING) {
    return {
      grandTotal: amount,
      pennyRoundingAmount: 0,
    };
  }

  const calcNum = (amount * scaleFactor).toFixed(0);
  const calcPrecision = (roundingScale * scaleFactor).toFixed(0);

  const remainder = Number(calcNum.slice(-calcPrecision.length)) % calcPrecision;

  let pennyRoundingAmount;
  if (remainder === 0) {
    pennyRoundingAmount = 0;
  } else if (remainder * 2 < calcPrecision) {
    pennyRoundingAmount = -remainder;
  } else if (remainder * 2 > calcPrecision) {
    pennyRoundingAmount = Number(calcPrecision) - remainder;
  } else {
    pennyRoundingAmount = roundingRule === ROUNDINGTYPES.ROUNDINGDOWN ? -remainder : remainder;
  }

  return {
    grandTotal: (Number(calcNum) + pennyRoundingAmount) / scaleFactor,
    pennyRoundingAmount: pennyRoundingAmount / scaleFactor,
  };
}

function hasDecimalPartGreaterThanZero(amount) {
  const decimalPart = amount % 1;

  return decimalPart > 0;
}

function isIntegerOnly(currencyCode) {
  const { formatConfig: { integerOnly } } = (currenciesConfig)[currencyCode];
  return integerOnly;
}

export default function roundAmountWithRules(amount, { roundingScale, roundingRule }, currencyCode = 'USD') {
  if (!isNumber(amount) || !isNumber(roundingScale)) {
    throw new Error('Input roundingRule error');
  }

  if (compare(roundingScale, 0.01) < 0) {
    throw new Error('Precision cannot be less than 0.01');
  }

  // 100.00 will be change to 100 by js auto so we should use currencyCode to ajust integer or decimal
  if (isIntegerOnly(currencyCode)) {
    if (isDecimal(amount) || hasDecimalPartGreaterThanZero(roundingScale)) {
      throw new Error('For integers, the roundingScale must also be an integer.');
    }
    if (String(amount).length > 15) {
      throw new Error('For integers, the length must not exceed 15 digits.');
    }
    return precisionAdjuster(Number(amount), { roundingRule, roundingScale: Number(roundingScale) });
  }

  const [ integerPart, decimalPart ] = String(amount).split('.');
  if (integerPart.length > 13) {
    throw new Error('For floating-point numbers, the integer part must not exceed 13 digits.');
  }
  if (decimalPart && decimalPart.length > 2) {
    throw new Error('For floating-point numbers, the decimal part must not exceed 2 digits.');
  }
  return precisionAdjuster(Number(amount), { roundingRule, roundingScale: Number(roundingScale), scaleFactor: 100 });
}
